import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Callout, FormSection, Icon, Paragraph, Title, toastError, toastSuccess } from 'admination-react'
import { AskModal } from '@/core/components/ui/ask-modal'
import { getAnwesenheitscode, neuerAnwesenheitscode, refusalToken } from '../../api/anwesenheit'

type Stand = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; code: string | null }

export function AnwesenheitscodeSection({ coachingId }: { coachingId: string }) {
  const { t } = useTranslation('vergabeStaff')
  const [stand, setStand] = useState<Stand>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const [fragen, setFragen] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    setStand({ kind: 'loading' })
    getAnwesenheitscode(coachingId)
      .then((code) => {
        if (!cancelled) setStand({ kind: 'ready', code })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        console.error("Anwesenheitscode laden fehlgeschlagen:", error)
        setStand({ kind: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [coachingId, attempt])

  async function ausstellen() {
    setBusy(true)
    try {
      const code = await neuerAnwesenheitscode(coachingId)
      setStand({ kind: 'ready', code })
      toastSuccess(t('profil.anwesenheitscode.erfolg'))
    } catch (error) {
      if (refusalToken(error)) toastError(t('profil.anwesenheitscode.gesperrt'))
      else {
        console.error("Anwesenheitscode ausstellen fehlgeschlagen:", error)
        toastError(t('profil.anwesenheitscode.fehler'))
      }
    } finally {
      setBusy(false)
      setFragen(false)
    }
  }

  return (
    <FormSection label={t('profil.anwesenheitscode.section')}>
      <Paragraph variant="muted">{t('profil.anwesenheitscode.hint')}</Paragraph>
      {stand.kind === 'loading' ? (
        <Paragraph variant="muted" aria-busy="true">
          {t('profil.loading')}
        </Paragraph>
      ) : stand.kind === 'error' ? (
        <>
          <Callout tone="error" role="alert">
            {t('profil.anwesenheitscode.ladeFehler')}
          </Callout>
          <div>
            <Button onClick={() => setAttempt((n) => n + 1)}>{t('profil.retry')}</Button>
          </div>
        </>
      ) : (
        <>
          {stand.code === null ? (
            <Paragraph>{t('profil.anwesenheitscode.keiner')}</Paragraph>
          ) : (
            <Title as="h2" data-testid="anwesenheitscode">{stand.code}</Title>
          )}
          <div>
            <Button
              data-testid="anwesenheitscode-ausstellen"
              disabled={busy}
              onClick={() => (stand.code === null ? void ausstellen() : setFragen(true))}
            >
              <Icon name="i-signature" />
              {stand.code === null ? t('profil.anwesenheitscode.ausstellen') : t('profil.anwesenheitscode.neu')}
            </Button>
          </div>
        </>
      )}
      <AskModal
        open={fragen}
        icon="i-signature"
        iconStroke
        variant="is-warning"
        question={t('profil.anwesenheitscode.frage')}
        description={t('profil.anwesenheitscode.frageText')}
        confirmLabel={t('profil.anwesenheitscode.neu')}
        cancelLabel={t('profil.rueckzug.cancel')}
        busy={busy}
        onConfirm={() => void ausstellen()}
        onCancel={() => setFragen(false)}
      />
    </FormSection>
  )
}
